<template>
  <div
    class="commitment-list-card"
    :data-testid="`commitment-list-card-${commitment.id}`"
  >
    <div class="flex items-start justify-between gap-3 flex-wrap">
      <div class="flex items-center gap-2 min-w-0 flex-1 basis-[12rem]">
        <span
          class="commitment-list-card__dot shrink-0"
          :style="{ backgroundColor: statusDotColor }"
        />
        <h3 class="commitment-list-card__title m-0">
          {{ commitment.was }}
        </h3>
      </div>

      <div class="flex items-center gap-2 shrink-0 ml-auto">
        <span
          class="commitment-list-card__badge"
          :class="badgeClass"
        >
          {{ badgeLabel }}
        </span>
        <div v-if="commitment.status === 'open'" class="relative">
          <button
            type="button"
            class="commitment-list-card__status-trigger"
            data-testid="commitment-list-status-trigger"
            @click="toggleDropdown"
          >
            <i
              class="pi pi-chevron-down text-xs transition-transform"
              :class="{ 'rotate-180': showDropdown }"
              aria-hidden="true"
            />
          </button>
          <div
            v-if="showDropdown"
            class="commitment-list-card__dropdown"
            data-testid="commitment-list-status-menu"
          >
            <button
              v-for="option in statusOptions"
              :key="option.value"
              type="button"
              class="commitment-list-card__dropdown-item"
              :data-testid="`commitment-list-status-option-${option.value}`"
              @click="selectStatus(option.value)"
            >
              <span
                class="commitment-list-card__status-dot"
                :style="{ backgroundColor: option.dot }"
              />
              <span>{{ option.label }}</span>
            </button>
          </div>
        </div>
        <Button
          v-if="commitment.status === 'open'"
          icon="pi pi-pencil"
          severity="secondary"
          text
          rounded
          :aria-label="$t('ProjectManagement.edit')"
          @click="$emit('edit', commitment)"
        />
        <Button
          v-if="commitment.status === 'open'"
          icon="pi pi-trash"
          severity="danger"
          text
          rounded
          :aria-label="$t('ProjectManagement.commitments.deleteTitle')"
          @click="$emit('delete', commitment)"
        />
      </div>
    </div>

    <div class="commitment-list-card__meta">
      <span>{{ dueLine }}</span>
      <span v-if="doneLine">· {{ doneLine }}</span>
      <span v-if="commitment.completionComment">· {{ commitment.completionComment }}</span>
      <span v-else-if="commitment.milestoneTitle">· {{ commitment.milestoneTitle }}</span>
    </div>

    <CommitmentDoneDialog
      v-model:visible="doneDialogVisible"
      :commitment-label="commitment.was"
      :loading="saving"
      @submit="submitDone"
    />

    <div class="commitment-list-card__owner">
      <span
        v-if="ownerInitials"
        class="commitment-list-card__avatar"
        :title="commitment.ownerDisplayName || ''"
      >
        {{ ownerInitials }}
      </span>
      <span v-if="commitment.ownerDisplayName" class="commitment-list-card__owner-name">
        {{ commitment.ownerDisplayName }}
      </span>
      <span
        v-if="constraintLinked"
        class="commitment-list-card__constraint-link"
      >
        ·
        <i class="pi pi-link text-xs" aria-hidden="true" />
        {{ $t('UserProjects.myCommitments.constraintLinked') }}
      </span>
    </div>

    <div
      v-if="showCauseForm"
      class="commitment-list-card__cause flex flex-col gap-3"
      data-testid="commitment-list-cause-form"
    >
      <label class="flex flex-col gap-1 text-sm">
        <span>{{ $t('ProjectManagement.commitments.cause.category') }}</span>
        <Select
          v-model="causeForm.category"
          :options="causeCategories"
          option-label="label"
          option-value="value"
          :placeholder="$t('ProjectManagement.commitments.cause.categoryPlaceholder')"
          class="w-full"
          data-testid="commitment-cause-category"
        />
      </label>
      <label class="flex flex-col gap-1 text-sm">
        <span>{{ $t('ProjectManagement.commitments.cause.freeText') }}</span>
        <Textarea v-model="causeForm.freeText" rows="2" class="w-full" />
      </label>
      <div class="flex items-center gap-2">
        <Checkbox v-model="causeForm.constraintRequested" binary input-id="list-cause-constraint" />
        <label for="list-cause-constraint" class="text-sm">
          {{ $t('ProjectManagement.commitments.cause.requestConstraint') }}
        </label>
      </div>
      <div class="flex gap-2 justify-end">
        <button type="button" class="commitment-list-card__btn-secondary" @click="cancelCause">
          {{ $t('ProjectManagement.cancel') }}
        </button>
        <button
          type="button"
          class="commitment-list-card__btn-primary btn-primary-accent"
          :disabled="saving"
          data-testid="commitment-cause-submit"
          @click="submitCause"
        >
          {{ $t('ProjectManagement.save') }}
        </button>
      </div>
    </div>
  </div>
</template>

<script>
import { mapActions } from 'vuex';
import Button from 'primevue/button';
import Select from 'primevue/select';
import Textarea from 'primevue/textarea';
import Checkbox from 'primevue/checkbox';
import { useToast } from 'primevue/usetoast';
import CommitmentDoneDialog from '@/components/CustomerApp/UserProjects/CommitmentDoneDialog.vue';

const CAUSE_CATEGORIES = [
  'blocked',
  'resource',
  'dependency',
  'scope',
  'other',
];

export default {
  name: 'CommitmentListCard',
  components: {
    Button,
    Select,
    Textarea,
    Checkbox,
    CommitmentDoneDialog,
  },
  props: {
    commitment: {
      type: Object,
      required: true,
    },
  },
  emits: ['updated', 'edit', 'delete'],
  setup() {
    const toast = useToast();
    return { toast };
  },
  data() {
    return {
      showDropdown: false,
      showCauseForm: false,
      doneDialogVisible: false,
      saving: false,
      causeForm: {
        category: null,
        freeText: '',
        constraintRequested: false,
      },
    };
  },
  computed: {
    displayStatus() {
      return this.showCauseForm ? 'not_done' : this.commitment.status;
    },
    badgeLabel() {
      if (this.displayStatus === 'not_done') {
        return this.$t('UserProjects.myCommitments.filters.notDone');
      }
      if (this.displayStatus === 'done') {
        return this.$t('UserProjects.myCommitments.filters.done');
      }
      if (this.commitment.isOverdue) {
        return this.$t('UserProjects.myCommitments.filters.overdue');
      }
      return this.$t('UserProjects.myCommitments.filters.open');
    },
    badgeClass() {
      if (this.displayStatus === 'not_done') return 'commitment-list-card__badge--not-done';
      if (this.displayStatus === 'done') return 'commitment-list-card__badge--done';
      if (this.commitment.isOverdue) return 'commitment-list-card__badge--overdue';
      return 'commitment-list-card__badge--open';
    },
    statusDotColor() {
      if (this.displayStatus === 'not_done') return '#EF4444';
      if (this.displayStatus === 'done') return '#22C55E';
      if (this.commitment.isOverdue) return '#EF4444';
      return '#e4821a';
    },
    ownerInitials() {
      const source = this.commitment.ownerDisplayName || '';
      if (!source) return '';
      const parts = source.trim().split(/\s+/);
      if (parts.length >= 2) {
        return `${parts[0][0]}${parts[1][0]}`.toUpperCase();
      }
      return source.slice(0, 2).toUpperCase();
    },
    dueLine() {
      if (!this.commitment.dueDate) {
        return this.$t('UserProjects.myCommitments.noDueDate');
      }
      return this.$t('UserProjects.myCommitments.dueLine', {
        date: this.formatCommitmentDate(this.commitment.dueDate),
      });
    },
    doneLine() {
      if (this.commitment.status !== 'done' || !this.commitment.doneDate) {
        return '';
      }
      return this.$t('UserProjects.myCommitments.doneLine', {
        date: this.formatCommitmentDate(this.commitment.doneDate),
      });
    },
    causeCategories() {
      return CAUSE_CATEGORIES.map((value) => ({
        value,
        label: this.$t(`ProjectManagement.commitments.cause.categories.${value}`),
      }));
    },
    statusOptions() {
      return [
        {
          value: 'done',
          label: this.$t('ProjectManagement.commitments.status.done'),
          dot: '#22C55E',
        },
        {
          value: 'not_done',
          label: this.$t('ProjectManagement.commitments.status.not_done'),
          dot: '#EF4444',
        },
      ];
    },
    constraintLinked() {
      return Boolean(this.commitment.cause?.constraintRequested);
    },
  },
  mounted() {
    document.addEventListener('click', this.handleOutsideClick);
  },
  beforeUnmount() {
    document.removeEventListener('click', this.handleOutsideClick);
  },
  methods: {
    ...mapActions('pmCommitment', ['updateCommitmentStatus', 'recordCause']),
    formatCommitmentDate(value) {
      return new Date(value).toLocaleDateString(undefined, {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric',
      });
    },
    toggleDropdown(event) {
      event.stopPropagation();
      this.showDropdown = !this.showDropdown;
    },
    handleOutsideClick() {
      this.showDropdown = false;
    },
    selectStatus(status) {
      this.showDropdown = false;
      if (status === 'done') {
        this.doneDialogVisible = true;
        return;
      }
      if (status === 'not_done') {
        this.openCauseForm();
      }
    },
    async submitDone({ completionComment }) {
      this.saving = true;
      try {
        await this.updateCommitmentStatus({
          id: this.commitment.id,
          status: 'done',
          completionComment,
        });
        this.toast.add({
          severity: 'success',
          summary: this.$t('ProjectManagement.commitments.toast.statusUpdated'),
          life: 3000,
        });
        this.doneDialogVisible = false;
        this.$emit('updated');
      } catch {
        this.toast.add({
          severity: 'error',
          summary: this.$t('ProjectManagement.commitments.toast.error'),
          life: 4000,
        });
      } finally {
        this.saving = false;
      }
    },
    async markDone() {
      this.doneDialogVisible = true;
    },
    openCauseForm() {
      this.causeForm = { category: null, freeText: '', constraintRequested: false };
      this.showCauseForm = true;
    },
    cancelCause() {
      this.showCauseForm = false;
    },
    async submitCause() {
      if (!this.causeForm.category) {
        this.toast.add({
          severity: 'warn',
          summary: this.$t('ProjectManagement.commitments.validation.causeCategoryRequired'),
          life: 4000,
        });
        return;
      }
      this.saving = true;
      try {
        await this.recordCause({
          id: this.commitment.id,
          payload: {
            category: this.causeForm.category,
            freeText: this.causeForm.freeText?.trim() || undefined,
            constraintRequested: this.causeForm.constraintRequested,
          },
        });
        await this.updateCommitmentStatus({ id: this.commitment.id, status: 'not_done' });
        this.toast.add({
          severity: 'success',
          summary: this.$t('ProjectManagement.commitments.toast.causeRecorded'),
          life: 3000,
        });
        this.showCauseForm = false;
        this.$emit('updated');
      } catch {
        this.toast.add({
          severity: 'error',
          summary: this.$t('ProjectManagement.commitments.toast.error'),
          life: 4000,
        });
      } finally {
        this.saving = false;
      }
    },
  },
};
</script>

<style scoped>
.commitment-list-card {
  background: #fff;
  border: 1px solid #e5e5e5;
  border-radius: 12px;
  padding: 1rem;
  box-shadow: 0 1px 2px rgba(0, 0, 0, 0.04);
}

.commitment-list-card__dot {
  width: 10px;
  height: 10px;
  border-radius: 999px;
}

.commitment-list-card__title {
  font-size: 15px;
  font-weight: 600;
  color: var(--color-dark);
  overflow-wrap: anywhere;
  min-width: 0;
}

.commitment-list-card__badge {
  display: inline-flex;
  align-items: center;
  padding: 0.125rem 0.625rem;
  border-radius: 999px;
  font-size: 11px;
  font-weight: 500;
}

.commitment-list-card__badge--open {
  background: #fff7ed;
  color: #e4821a;
}

.commitment-list-card__badge--overdue,
.commitment-list-card__badge--not-done {
  background: #fef2f2;
  color: #ef4444;
}

.commitment-list-card__badge--done {
  background: #f0fdf4;
  color: #22c55e;
}

.commitment-list-card__status-trigger {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 1.75rem;
  height: 1.75rem;
  border: none;
  border-radius: 8px;
  background: transparent;
  color: var(--color-disabled);
  cursor: pointer;
}

.commitment-list-card__status-trigger:hover {
  background: #f5f5f5;
}

.commitment-list-card__dropdown {
  position: absolute;
  right: 0;
  top: calc(100% + 4px);
  z-index: 20;
  min-width: 180px;
  background: #fff;
  border: 1px solid #e5e5e5;
  border-radius: 10px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
  padding: 0.25rem 0;
}

.commitment-list-card__dropdown-item {
  display: flex;
  align-items: center;
  gap: 0.625rem;
  width: 100%;
  padding: 0.5rem 0.75rem;
  border: none;
  background: transparent;
  color: var(--color-dark);
  font-size: 0.875rem;
  text-align: left;
  cursor: pointer;
}

.commitment-list-card__dropdown-item:hover {
  background: #f5f5f5;
}

.commitment-list-card__status-dot {
  width: 10px;
  height: 10px;
  border-radius: 999px;
  flex-shrink: 0;
}

.commitment-list-card__meta {
  display: flex;
  flex-wrap: wrap;
  gap: 0.25rem;
  margin-top: 0.375rem;
  font-size: 13px;
  color: var(--color-disabled);
}

.commitment-list-card__owner {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 0.5rem;
  margin-top: 0.5rem;
}

.commitment-list-card__avatar {
  width: 1.25rem;
  height: 1.25rem;
  border-radius: 999px;
  background: var(--color-orange);
  color: #fff;
  font-size: 10px;
  font-weight: 600;
  display: inline-flex;
  align-items: center;
  justify-content: center;
}

.commitment-list-card__owner-name,
.commitment-list-card__constraint-link {
  font-size: 12px;
  color: var(--color-disabled);
}

.commitment-list-card__cause {
  margin-top: 0.75rem;
  padding-top: 0.75rem;
  border-top: 1px solid #e5e5e5;
}

.commitment-list-card__btn-primary {
  display: inline-flex;
  align-items: center;
  padding: 0.375rem 0.75rem;
  border: none;
  border-radius: 10px;
  font-size: 0.75rem;
  font-weight: 500;
  cursor: pointer;
}

.commitment-list-card__btn-secondary {
  display: inline-flex;
  align-items: center;
  padding: 0.375rem 0.75rem;
  border: 1px solid #e5e5e5;
  border-radius: 10px;
  background: #fff;
  color: var(--color-dark);
  font-size: 0.75rem;
  font-weight: 500;
  cursor: pointer;
}

.commitment-list-card__btn-secondary:hover {
  background: #f5f5f5;
}
</style>
